<template>
  <Disclosure v-slot="{ open }" as="div">
    <DisclosureButton
      class="flex h-[56px] w-full items-center justify-between px-4 text-left hover:bg-gray-100"
    >
      <div class="flex items-center space-x-3">
        <div class="flex h-8 w-8 items-center justify-center">
          <font-awesome-icon
            size="lg"
            class="w-5 shrink-0 text-gray-400"
            aria-hidden="true"
            :icon="icon"
          />
        </div>
        <span class="text-sm text-gray-700">{{ title }}</span>
      </div>
      <font-awesome-icon
        size="sm"
        class="w-4 shrink-0 text-gray-400 transition-transform duration-200"
        :class="{ 'rotate-90 transform': open }"
        aria-hidden="true"
        :icon="['fal', 'chevron-right']"
      />
    </DisclosureButton>

    <transition
      enter-active-class="transition duration-100 ease-out"
      enter-from-class="transform scale-95 opacity-0"
      enter-to-class="transform scale-100 opacity-100"
      leave-active-class="transition duration-75 ease-in"
      leave-from-class="transform scale-100 opacity-100"
      leave-to-class="transform scale-95 opacity-0"
    >
      <DisclosurePanel class="bg-gray-50">
        <SelectListComponent
          v-if="showLanguageSelector && hasLanguageOptions"
          :items="languageItems"
          :model-value="selectedLanguage"
          :use-flag="true"
          align="right"
          @update:model-value="handleLanguageChange"
        />
      </DisclosurePanel>
    </transition>
  </Disclosure>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'
import { useAppStore, useLanguageStore } from '@/stores'
import type { SelectItem } from '@/interfaces/components/ISelectComponent'
import type { ILanguageItem } from '@/interfaces/ILanguageItem'

defineProps<{
  icon: [string, string]
  title: string
  selectedLanguage?: SelectItem
}>()

const emit = defineEmits<{
  'language-change': [language: SelectItem]
}>()

// Get stores
const appStore = useAppStore()
const languageStore = useLanguageStore()

// Computed properties
const showLanguageSelector = computed(() => appStore.showLanguageSelector === true)
const hasLanguageOptions = computed(
  () => languageStore.listOfAvailableLanguages && languageStore.listOfAvailableLanguages.length > 1
)

const languageItems = computed<SelectItem[]>(() => {
  return (
    languageStore.listOfAvailableLanguages?.map((item: ILanguageItem) => ({
      name: item.name,
      id: item.locale,
      image: item.image
    })) || []
  )
})

// Event handler
const handleLanguageChange = (language: SelectItem) => {
  emit('language-change', language)
}
</script>
